import { Brand, ErrorResponse } from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, ApiClientError, csrfHeaders } from './api';
import { api } from './api';

export const brandKey = ['brand'] as const;

export const useBrand = (enabled = true) =>
  useQuery({ queryKey: brandKey, queryFn: () => api.get('/brand', Brand), enabled });

/** Multipart PUT of one file (logo or a kit slot); the brand comes back. */
async function putFile(path: string, file: File): Promise<Brand> {
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    body,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...csrfHeaders() },
  });
  const json: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const err = ErrorResponse.safeParse(json);
    throw err.success
      ? new ApiClientError(
          res.status,
          err.data.error.code,
          err.data.error.message,
          err.data.error.details,
        )
      : new ApiClientError(res.status, 'internal_error', `HTTP ${res.status}`);
  }
  return Brand.parse(json);
}

export function useBrandUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { slot: 'logo' | Brand['kit'][number]['slot']; file: File | null }) =>
      v.file
        ? putFile(v.slot === 'logo' ? '/settings/logo' : `/settings/brand-kit/${v.slot}`, v.file)
        : api.delete('/settings/logo', Brand),
    onSuccess: (b) => qc.setQueryData(brandKey, b),
  });
}

/** The logo to show: the uploaded one, else the kit variant for the theme. */
export function useLogoSrc(fallback: string): string {
  const brand = useBrand();
  return brand.data?.logo.custom ? brand.data.logo.url : fallback;
}
